import { Formik } from "formik";
import * as Yup from "yup";
import {
  Grid,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  createFilterOptions,
  Autocomplete,
  FormHelperText,
  Checkbox,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import { ClearIcon, DatePicker } from "@mui/x-date-pickers";
import cloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";
import {
  GENDER_IDENTITY_OPTIONS,
  GENDER_OPTIONS,
  SEX_ASSIGNED_AT_BIRTH_OPTIONS,
  UCH_CLINICS,
  USER_ROLES,
  MARITAL_STATUS_OPTIONS,
  PREFERRED_LANGUAGE_OPTIONS,
  CLINIC_NAMES,
  EVOKE_ROLES,
} from "static/constants";
import SubmitButton from "components/Button/SubmitButton";
import usePatientModal from "../../../usePatientModal";
import { isValidArray } from "utils/array";
import { getFormattedDate, isValidDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import { getOptionValue } from "utils/autocomplete";
import { toggleDeactivate } from "app/store/transmission/slice/transmissionSlice";
import SSNMask from "components/common/input/SSNMask";
import { getCurrentUserRole } from "utils/currentUser.util";
import FormControlWithTooltip from "components/FormFields/FormControlWithTooltip";
import { useMemo } from "react";

// Define validation schema using Yup
const validationSchema = (deviceInformation) => {
  return Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    dob: Yup.string()
      .required("Date of Birth is required")
      .test("is-valid-date", "Date of Birth must be a valid date", (value) =>
        isValidDate(value)
      ),
    doi:
      deviceInformation?.rpm_devices?.length === 0
        ? Yup.string()
            .required("Date of Implant is required")
            .test(
              "is-valid-date",
              "Date of Implant must be a valid date",
              (value) => isValidDate(value)
            )
        : Yup.string().nullable(),
    gender: Yup.string().label("Gender"),
    genderIdentity: Yup.string(),
    marital_status: Yup.string(),
    ssn: Yup.string().matches(
      /^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/,
      "Invalid SSN format"
    ),
    sexAssignedAtBirth: Yup.string(),
    mrn: Yup.string(),
    account: Yup.string().required("Account is required"),
    preferredLanguage: Yup.array()
      .max(2, "Maximum 2 languages are allowed")
      .test(
        "validate-language-selection",
        "When selecting 2 languages, one must be English. For example: English + Spanish or English + French.",
        (value) => !value || value.length < 2 || value.includes("English")
      )
      .typeError("Preferred Language must be an array"),
  });
};

const PatientBasicInfoForm = ({
  setIsEditMode,
  handleSave,
  isLoading,
  handleCancel,
}) => {
  const {
    currentUser,
    profile,
    accounts,
    accountsMapper,
    hospitals,
    newProfileInfo,
    physicianData,
    originalProfile,
    referringProviders,
    newPatientAlertInformation,
    handleAlertInputChange,
    handleInputChange,
    deviceInformation,
    dispatch,
  } = usePatientModal();

  const userRole = getCurrentUserRole();
  const autoCompleteFilter = createFilterOptions();

  const physicianOptions = useMemo(() => {
    return (
      physicianData?.filter((physician) =>
        physician.accounts?.includes(newProfileInfo.account)
      ) || []
    );
  }, [physicianData, newProfileInfo.account]);

  const selectedPhysician =
    physicianOptions.find(
      (physician) =>
        physician.userId === newProfileInfo?.physicians?.[0]?.user_id
    ) || null;

  return (
    <Formik
      initialValues={{ ...newProfileInfo }}
      validationSchema={validationSchema(deviceInformation)}
      onSubmit={(values, { setSubmitting }) => {
        handleSave(values);
        setSubmitting(false);
      }}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* First Name */}
            <Grid item xs={3}>
              <TextField
                variant="outlined"
                margin="none"
                fullWidth
                label="First Name"
                name="firstName"
                data-text="patient-basic-info-first-name-field"
                value={newProfileInfo.firstName}
                onChange={(event) => {
                  const value = event.target.value;
                  const copy = cloneDeep(newProfileInfo);
                  copy.firstName = value;
                  handleInputChange(copy);
                  setFieldValue("firstName", value);
                }}
                onBlur={handleBlur}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={3}>
              <TextField
                variant="outlined"
                margin="none"
                fullWidth
                label="Last Name"
                name="lastName"
                data-text="patient-basic-info-last-name-field"
                value={newProfileInfo.lastName}
                onChange={(event) => {
                  const value = event.target.value;
                  const copy = cloneDeep(newProfileInfo);
                  copy.lastName = value;
                  handleInputChange(copy);
                  setFieldValue("lastName", value);
                }}
                onBlur={handleBlur}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
              />
            </Grid>

            {/* Middle Initial */}
            <Grid item xs={3}>
              <TextField
                variant="outlined"
                margin="none"
                fullWidth
                label="MI"
                name="middleInitial"
                data-text="patient-basic-info-middle-initial-field"
                value={newProfileInfo.middleInitial}
                onChange={(event) => {
                  const value = event.target.value;
                  const copy = cloneDeep(newProfileInfo);
                  copy.middleInitial = value;
                  handleInputChange(copy);
                  setFieldValue("middleInitial", value);
                }}
                onBlur={handleBlur}
              />
            </Grid>

            {/* Evoke ID (disabled) */}
            <Grid item xs={3}>
              <TextField
                variant="outlined"
                margin="none"
                fullWidth
                label="Evoke ID"
                name="evokeId"
                value={newProfileInfo.evokeId}
                onChange={(event) => {
                  const value = event.target.value;
                  const copy = cloneDeep(newProfileInfo);
                  copy.evokeId = value;
                  handleInputChange(copy);
                  setFieldValue("evokeId", value);
                }}
                disabled
              />
            </Grid>

            {/* Date of Birth */}
            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <DatePicker
                  inputVariant="outlined"
                  id="dob"
                  label="Date of Birth"
                  className="trans-table__filterDatePicker"
                  name="dob"
                  data-text="patient-basic-info-date-of-birth-field"
                  format={DATE_FORMAT.DEFAULT}
                  KeyboardButtonProps={{
                    "aria-label": "pick date",
                  }}
                  value={
                    newProfileInfo?.dob ? dayjs(newProfileInfo?.dob) : null
                  }
                  onChange={(value) => {
                    const formattedDate = getFormattedDate(
                      value,
                      DATE_FORMAT.DEFAULT
                    );
                    const copy = { ...newProfileInfo, dob: formattedDate };

                    handleInputChange(copy);
                    setFieldValue("dob", formattedDate);
                  }}
                  maxDate={dayjs(new Date())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: touched.dob && errors.dob,
                      error: touched.dob && Boolean(errors.dob),
                    },
                  }}
                />
              </FormControl>
            </Grid>

            {/* Gender */}
            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <InputLabel>Gender (Legal)</InputLabel>
                <Select
                  name="gender"
                  data-action="patient-basic-info-gender-field"
                  value={newProfileInfo.gender}
                  onChange={(event) => {
                    const value = event.target.value;
                    const copy = cloneDeep(newProfileInfo);
                    copy.gender = value;
                    handleInputChange(copy);
                    setFieldValue("gender", value);
                  }}
                  onBlur={handleBlur}
                  label="Gender (Legal)"
                  error={touched.gender && Boolean(errors.gender)}
                >
                  {GENDER_OPTIONS.map(({ option, value }, index) => (
                    <MenuItem key={index} value={value}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>

                {touched.gender && Boolean(errors.gender) && (
                  <FormHelperText error>{errors.gender}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Gender Identity */}
            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <InputLabel>Gender Identity</InputLabel>
                <Select
                  name="genderIdentity"
                  label="Gender Identity"
                  data-action="patient-basic-info-gender-identity-field"
                  value={newProfileInfo.genderIdentity}
                  variant="outlined"
                  onChange={(event) => {
                    const value = event.target.value;
                    const copy = cloneDeep(newProfileInfo);
                    copy.genderIdentity = value;
                    handleInputChange(copy);
                    setFieldValue("genderIdentity", value);
                  }}
                  onBlur={handleBlur}
                >
                  {GENDER_IDENTITY_OPTIONS.map(({ option, value }, index) => (
                    <MenuItem key={index} value={value}>
                      {option}
                    </MenuItem>
                  ))}
                  {touched.genderIdentity && Boolean(errors.genderIdentity) && (
                    <FormHelperText error>
                      {errors.genderIdentity}
                    </FormHelperText>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <InputLabel>Marital Status</InputLabel>
                <Select
                  name="marital_status"
                  label="Marital Status"
                  data-action="patient-basic-info-marital-status-field"
                  value={newProfileInfo.marital_status}
                  variant="outlined"
                  onChange={(event) => {
                    const value = event.target.value;
                    const copy = cloneDeep(newProfileInfo);
                    copy.marital_status = value;
                    handleInputChange(copy);
                    setFieldValue("marital_status", value);
                  }}
                  onBlur={handleBlur}
                >
                  {MARITAL_STATUS_OPTIONS.map(({ option, value }, index) => (
                    <MenuItem key={index} value={value}>
                      {option}
                    </MenuItem>
                  ))}
                  {touched.marital_status && Boolean(errors.marital_status) && (
                    <FormHelperText error>
                      {errors.marital_status}
                    </FormHelperText>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={3}>
              <TextField
                variant="outlined"
                margin="none"
                fullWidth
                label="Social Security Number"
                data-text="patient-basic-info-ssn-field"
                name="ssn"
                value={newProfileInfo.ssn || ""}
                onChange={(event) => {
                  const value = event.target.value;
                  const copy = cloneDeep(newProfileInfo);
                  copy.ssn = value;
                  handleInputChange(copy);
                  setFieldValue("ssn", value);
                }}
                onBlur={handleBlur}
                error={touched.ssn && Boolean(errors.ssn)}
                helperText={touched.ssn && errors.ssn}
                InputProps={{ inputComponent: SSNMask }}
              />
            </Grid>

            {/* Sex Assigned At Birth */}
            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <InputLabel>Sex Assigned At Birth</InputLabel>
                <Select
                  label="Sex Assigned At Birth"
                  name="sexAssignedAtBirth"
                  data-action="patient-basic-info-sex-assigned-at-birth-field"
                  value={newProfileInfo.sexAssignedAtBirth}
                  variant="outlined"
                  onChange={(event) => {
                    const value = event.target.value;
                    const copy = cloneDeep(newProfileInfo);
                    copy.sexAssignedAtBirth = value;

                    handleInputChange(copy);
                    setFieldValue("sexAssignedAtBirth", value);
                  }}
                  onBlur={handleBlur}
                >
                  {SEX_ASSIGNED_AT_BIRTH_OPTIONS.map(
                    ({ option, value }, index) => (
                      <MenuItem key={index} value={value}>
                        {option}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <DatePicker
                  inputVariant="outlined"
                  id="doi"
                  label="Date of Implant"
                  data-text="patient-basic-info-date-of-implant-field"
                  className="trans-table__filterDatePicker"
                  name="doi"
                  format={DATE_FORMAT.DEFAULT}
                  KeyboardButtonProps={{
                    "aria-label": "pick date",
                  }}
                  value={
                    newProfileInfo?.doi ? dayjs(newProfileInfo?.doi) : null
                  }
                  onChange={(value) => {
                    const formattedDate = getFormattedDate(
                      value,
                      DATE_FORMAT.DEFAULT
                    );

                    const copy = cloneDeep(newProfileInfo);
                    copy.doi = formattedDate;
                    handleInputChange(copy);

                    setFieldValue("doi", formattedDate);
                  }}
                  maxDate={dayjs(new Date())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: deviceInformation?.rpm_devices?.length === 0,
                      helperText: touched.doi && errors.doi,
                      error: touched.doi && Boolean(errors.doi),
                    },
                  }}
                />
              </FormControl>
            </Grid>

            {/* MRN */}
            <Grid item xs={3}>
              <TextField
                variant="outlined"
                margin="none"
                fullWidth
                label="MRN"
                name="mrn"
                data-text="patient-basic-info-mrn-field"
                value={newProfileInfo.mrn}
                onChange={(event) => {
                  const value = event.target.value;
                  const copy = cloneDeep(newProfileInfo);
                  copy.mrn = value;

                  handleInputChange(copy);
                  setFieldValue("mrn", value);
                }}
                onBlur={handleBlur}
                error={touched.mrn && Boolean(errors.mrn)}
                helperText={touched.mrn && errors.mrn}
              />
            </Grid>

            {/* Account */}
            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <InputLabel>Account</InputLabel>
                <Select
                  label="Account"
                  name="account"
                  data-action="patient-basic-info-account-field"
                  value={newProfileInfo.account}
                  onChange={(event) => {
                    const value = event.target.value;
                    const copy = cloneDeep(newProfileInfo);
                    copy.account = value;
                    setFieldValue("account", value);

                    if (profile.account !== copy.account) {
                      copy.physicians = [];
                      setFieldValue("physicians", []);

                      copy.referringProviderId = "";
                      setFieldValue("referringProviderId", "");
                    }

                    copy.statusLetter =
                      accountsMapper?.[value]?.settings?.status_letter || false;
                    setFieldValue("statusLetter", copy.statusLetter);

                    handleInputChange(copy);
                  }}
                  onBlur={handleBlur}
                  error={touched.account && Boolean(errors.account)}
                >
                  {accounts &&
                    accounts.map((account, index) => (
                      <MenuItem key={index} value={account?.name}>
                        {account?.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Following Physician */}
            <Grid item xs={3}>
              <FormControl
                fullWidth
                margin="none"
                error={touched.physicians && Boolean(errors.physicians)}
              >
                <Autocomplete
                  id="physicians"
                  name="physicians"
                  data-action="patient-basic-info-following-physician-field"
                  value={selectedPhysician}
                  options={physicianOptions}
                  onChange={(_event, selectedOption) => {
                    const copy = cloneDeep(newProfileInfo);
                    copy.physicians = selectedOption
                      ? [
                          {
                            user_id: selectedOption.userId,
                            name: selectedOption.name,
                          },
                        ]
                      : [];

                    handleInputChange(copy);
                    setFieldValue("physicians", copy.physicians);
                  }}
                  onBlur={handleBlur}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.userId === value?.userId
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Following Physician"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="referringProviderId"
                  name="referringProviderId"
                  data-action="patient-basic-info-referring-provider-field"
                  openOnFocus={true}
                  value={
                    referringProviders?.filter(
                      (referringProvider) =>
                        referringProvider._id ===
                        newProfileInfo.referringProviderId
                    )?.[0]?.name || ""
                  }
                  options={
                    isValidArray(referringProviders)
                      ? referringProviders
                          .filter(
                            (referringProvider) =>
                              referringProvider.account === profile.account ||
                              originalProfile.referringProviderId ===
                                referringProvider._id
                          )
                          .map((referringProvider) => ({
                            label: referringProvider.name,
                            id: referringProvider._id,
                          }))
                      : []
                  }
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{ padding: "8px 16px" }}
                      key={option.id}
                    >
                      {option.label}
                    </li>
                  )}
                  onChange={(e, newValue) => {
                    const value = newValue?.id || "";
                    const copy = cloneDeep(newProfileInfo);
                    copy.referringProviderId = value;

                    handleInputChange(copy);
                    setFieldValue("referringProviderId", value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Referring Provider"
                      variant="outlined"
                      placeholder="Referring Provider"
                      onBlur={handleBlur}
                      error={
                        touched.referringProviderId &&
                        Boolean(errors.referringProviderId)
                      }
                      helperText={
                        touched.referringProviderId &&
                        errors.referringProviderId
                      }
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={3}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  freeSolo
                  id="hospital"
                  name="hospital"
                  data-action="patient-basic-info-hospital-field"
                  value={newProfileInfo?.hospital}
                  options={
                    isValidArray(hospitals)
                      ? hospitals?.map(({ name }) => ({
                          label: name,
                          inputValue: name,
                        }))
                      : []
                  }
                  autoSelect
                  openOnFocus={true}
                  onChange={(e, newValue) => {
                    const selectedHospital =
                      typeof newValue === "string"
                        ? newValue
                        : newValue?.inputValue || newValue?.label;

                    const copy = cloneDeep(newProfileInfo);
                    copy.hospital = selectedHospital;

                    handleInputChange(copy);
                    setFieldValue("hospital", selectedHospital || "");
                  }}
                  filterOptions={(options, params) => {
                    const filtered = autoCompleteFilter(options, params);

                    if (params.inputValue !== "") {
                      filtered.unshift({
                        inputValue: params.inputValue,
                        label: `+ Add "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  getOptionLabel={getOptionValue}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Hospital"
                      placeholder="Hospital"
                      error={touched.hospital && Boolean(errors.hospital)}
                      helperText={touched.hospital && errors.hospital}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid
              container
              spacing={2}
              alignItems="flex-start"
              padding={0}
              margin={0}
            >
              <Grid item xs={3}>
                <FormControl
                  fullWidth
                  margin="none"
                  error={
                    touched?.preferredLanguage &&
                    Boolean(errors?.preferredLanguage)
                  }
                >
                  <InputLabel>Preferred Language</InputLabel>

                  <Select
                    name="preferredLanguage"
                    label="Preferred Language"
                    data-action="patient-basic-info-preferred-language-field"
                    multiple
                    value={
                      Array.isArray(newProfileInfo?.preferredLanguage)
                        ? newProfileInfo.preferredLanguage.filter(Boolean)
                        : newProfileInfo?.preferredLanguage
                          ? [newProfileInfo.preferredLanguage]
                          : []
                    }
                    onChange={(event) => {
                      const value = event.target.value || [];

                      const copy = cloneDeep(newProfileInfo);
                      copy.preferredLanguage = value.filter(Boolean);

                      handleInputChange(copy);
                      setFieldValue(
                        "preferredLanguage",
                        copy.preferredLanguage
                      );
                    }}
                    onBlur={handleBlur}
                    renderValue={(selected = []) => {
                      const validSelected = selected.filter(Boolean);

                      if (!validSelected.length) {
                        return null;
                      }

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            alignItems: "center",
                          }}
                        >
                          {validSelected.slice(0, 2).map((value) => (
                            <Chip
                              key={value}
                              variant="outlined"
                              label={
                                PREFERRED_LANGUAGE_OPTIONS.find(
                                  (opt) => opt.value === value
                                )?.option || value
                              }
                              deleteIcon={
                                <ClearIcon
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              }
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              onDelete={() => {
                                const newValues = validSelected.filter(
                                  (v) => v !== value
                                );

                                const copy = cloneDeep(newProfileInfo);
                                copy.preferredLanguage = newValues;

                                handleInputChange(copy);
                                setFieldValue("preferredLanguage", newValues);
                              }}
                              size="small"
                            />
                          ))}

                          {validSelected.length > 2 && (
                            <span>+{validSelected.length - 2}</span>
                          )}
                        </Box>
                      );
                    }}
                    sx={{
                      "& .MuiSelect-select": {
                        padding:
                          (Array.isArray(newProfileInfo?.preferredLanguage)
                            ? newProfileInfo.preferredLanguage.length
                            : 0) > 0
                            ? "11px 16px"
                            : "16px",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxWidth: 320,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      disableRipple
                      disableTouchRipple
                      sx={{
                        pointerEvents: "none",
                        cursor: "default",
                        whiteSpace: "normal",
                        lineHeight: 1.4,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                        "&.Mui-focusVisible": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Maximum 2 languages are allowed. When selecting 2
                        languages, one must be English (e.g., English + Spanish
                        or English + French).
                      </Typography>
                    </MenuItem>
                    {PREFERRED_LANGUAGE_OPTIONS.map(({ option, value }) => (
                      <MenuItem key={value} value={value}>
                        <Checkbox
                          checked={(
                            newProfileInfo?.preferredLanguage || []
                          ).includes(value)}
                          sx={{ mr: 1 }}
                        />
                        {option}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText
                    error={
                      touched?.preferredLanguage &&
                      Boolean(errors?.preferredLanguage)
                    }
                  >
                    {touched?.preferredLanguage && errors?.preferredLanguage}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Patient Alert */}
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  margin="none"
                  variant="outlined"
                  label="Patient Alert"
                  name="alertPatientText"
                  data-text="patient-basic-info-patient-alert-field"
                  value={newPatientAlertInformation.alertPatientText || null}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFieldValue("alertPatient", event);

                    const copy = cloneDeep(newPatientAlertInformation);
                    copy.alertPatientText = value;
                    handleAlertInputChange(copy);
                  }}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={3} marginTop={1}>
                <FormControlLabel
                  label="Enable Patient Alert"
                  control={
                    <Switch
                      name="alertPatient"
                      data-action="patient-basic-info-patient-alert-toggle"
                      checked={newPatientAlertInformation.alertPatient}
                      onChange={(event, checked) => {
                        setFieldValue("alertPatient", checked);

                        const copy = cloneDeep(newPatientAlertInformation);
                        copy.alertPatient = checked;
                        handleAlertInputChange(copy);
                      }}
                      onBlur={handleBlur}
                    />
                  }
                />
              </Grid>

              <Grid item xs={3} marginTop={1}>
                <FormControlLabel
                  label="Need magnet during procedures"
                  control={
                    <Switch
                      name="need_magnet_during_procedures"
                      data-action="patient-basic-info-magnet-toggle"
                      checked={newProfileInfo?.need_magnet_during_procedures}
                      onChange={(_event, checked) => {
                        const copy = cloneDeep(newProfileInfo);
                        copy.need_magnet_during_procedures = checked;

                        handleInputChange(copy);
                        setFieldValue("need_magnet_during_procedures", checked);
                      }}
                      onBlur={handleBlur}
                    />
                  }
                />
              </Grid>
            </Grid>

            <Grid item xs={3} alignSelf="center">
              <FormControlLabel
                label="LAAO Device"
                control={
                  <Switch
                    name="laao_device"
                    data-action="patient-basic-info-laao-device-toggle"
                    checked={newProfileInfo?.laao_device}
                    onChange={(_event, checked) => {
                      const copy = cloneDeep(newProfileInfo);
                      copy.laao_device = checked;

                      handleInputChange(copy);
                      setFieldValue("laao_device", checked);
                    }}
                    onBlur={handleBlur}
                  />
                }
              />
            </Grid>

            <Grid item xs={3} alignSelf="center">
              <FormControlLabel
                label="IDTF"
                control={
                  <Switch
                    name="idtf"
                    checked={newProfileInfo?.idtf}
                    onChange={(_event, checked) => {
                      const copy = cloneDeep(newProfileInfo);
                      copy.idtf = checked;

                      handleInputChange(copy);
                      setFieldValue("idtf", checked);
                    }}
                    onBlur={handleBlur}
                    disabled={
                      !(
                        accountsMapper[newProfileInfo?.account]?.settings
                          ?.billing_type === "SPLIT"
                      )
                    }
                  />
                }
              />
            </Grid>

            {EVOKE_ROLES.includes(userRole) && <FormControlWithTooltip
              label="Alerts only"
              control={
                <Switch
                  name="alertsOnly"
                  disabled={userRole !== USER_ROLES.ADMIN}
                  checked={newProfileInfo?.alertsOnly || false}
                  onChange={(_event, checked) => {
                    const copy = cloneDeep(newProfileInfo);
                    copy.alertsOnly = checked;

                    handleInputChange(copy);
                    setFieldValue("alertsOnly", checked);
                  }}
                />
              }
              tooltipText="Disables billing schedule and subscription settings"
              tooltipPosition="after"
              gridProps={{
                item: true,
                xs: 3,
              }}
            />}

            <Grid container item xs={3} alignSelf="center">
              <FormControlLabel
                label={
                  <div className="multiline-label">
                    Patient Active
                    {!newProfileInfo?.active &&
                    newProfileInfo?.deactivate_reason ? (
                      <span className="ml-1">
                        <span>- {newProfileInfo?.deactivate_reason}</span>
                        <span
                          className="profile-edit-button ml-1"
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            dispatch(toggleDeactivate({ isDeactivate: true }));
                          }}
                        >
                          (Change)
                        </span>
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                }
                style={{ marginRight: 0 }}
                control={
                  <Switch
                    name="active"
                    data-action="patient-basic-info-patient-active-toggle"
                    checked={newProfileInfo.active}
                    onChange={(event, checked) => {
                      const copy = cloneDeep(newProfileInfo);
                      copy.active = checked;
                      handleInputChange(copy);
                      if (!checked) {
                        dispatch(toggleDeactivate({ isDeactivate: !checked }));
                      }
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={3} alignSelf="center">
              <FormControlLabel
                label="Alert When Disconnected"
                control={
                  <Switch
                    name="alert_when_disconnected"
                    disabled={userRole !== USER_ROLES.ADMIN}
                    checked={newProfileInfo?.alertWhenDisconnected || false}
                    onChange={(_event, checked) => {
                      const copy = cloneDeep(newProfileInfo);
                      copy.alertWhenDisconnected = checked;

                      handleInputChange(copy);
                      setFieldValue("alertWhenDisconnected", checked);
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={3} alignSelf="center">
              <FormControlLabel
                label="Monthly Battery Checks"
                control={
                  <Switch
                    name="monthly_battery_check"
                    checked={newProfileInfo?.monthlyBatteryCheck || false}
                    onChange={(_event, checked) => {
                      const copy = cloneDeep(newProfileInfo);
                      copy.monthlyBatteryCheck = checked;

                      handleInputChange(copy);
                      setFieldValue("monthlyBatteryCheck", checked);
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={3} alignSelf="center">
              <FormControlLabel
                label="In Office Only"
                control={
                  <Switch
                    name="inOfficeOnly"
                    checked={newProfileInfo?.inOfficeOnly || false}
                    onChange={(_event, checked) => {
                      const copy = cloneDeep(newProfileInfo);
                      copy.inOfficeOnly = checked;

                      handleInputChange(copy);
                      setFieldValue("inOfficeOnly", checked);
                    }}
                  />
                }
              />
            </Grid>

            {newProfileInfo?.account === CLINIC_NAMES.PUEBLO && (
              <Grid item xs={3} alignSelf="center">
                <FormControlLabel
                  label="Status Letter"
                  control={
                    <Switch
                      name="statusLetter"
                      checked={newProfileInfo?.statusLetter || false}
                      onChange={(_event, checked) => {
                        const copy = cloneDeep(newProfileInfo);
                        copy.statusLetter = checked;

                        handleInputChange(copy);
                        setFieldValue("statusLetter", checked);
                      }}
                    />
                  }
                />
              </Grid>
            )}

            {[
              USER_ROLES.USER,
              USER_ROLES.ADMIN,
              USER_ROLES.CLINIC_ADMIN,
            ].includes(currentUser.role) &&
              UCH_CLINICS.includes(profile.account) && (
                <Grid
                  container
                  item
                  xs={12}
                  className="d-flex flex-column"
                  style={{
                    borderTop: "1px solid #e0e0e0",
                    paddingTop: "10px",
                    marginTop: "10px",
                  }}
                >
                  <Grid item xs={12}>
                    <div className="patient-form__info--header mb-0">
                      Process HF Report Direct To Clinic
                    </div>
                    <div className="text-normal text-grey mb-2">
                      Toggle to select HF report should be transmitted to the
                      clinic
                    </div>
                  </Grid>
                  <Grid container xs={12}>
                    <Grid item xs={3} style={{ textAlign: "left" }}>
                      <FormControlLabel
                        label="HF"
                        control={
                          <Switch
                            name="hfToClinic"
                            checked={newProfileInfo.hfToClinic}
                            onChange={(event, checked) => {
                              const copy = cloneDeep(newProfileInfo);
                              copy.hfToClinic = checked;
                              handleInputChange(copy);
                              setFieldValue("hfToClinic", checked);
                            }}
                          />
                        }
                      />
                    </Grid>
                    {/* <Grid item xs={3} style={{ textAlign: "left" }}>
                <FormControlLabel
                  label="Regular"
                  control={
                    <Switch
                      name="regularToClinic"
                      checked={newProfileInfo.regularToClinic}
                      onChange={(event, checked) => {
                        const copy = cloneDeep(newProfileInfo);
                        copy.regularToClinic = checked;
                        handleInputChange(copy);
                      }}
                    />
                  }
                />
              </Grid> */}
                  </Grid>
                </Grid>
              )}
          </Grid>

          {/* Save and Cancel buttons */}
          <div className="patient-form__action">
            <Button
              onClick={() => {
                handleCancel();
                setIsEditMode(false);
              }}
              variant="outlined"
              color="secondary"
              data-action="patient-basic-info-cancel-button"
            >
              Cancel
            </Button>
            <SubmitButton
              style={{ marginLeft: "10px" }}
              handleClick={(e) => handleSubmit(e)}
              disabled={isLoading}
              data-action="patient-basic-info-save-button"
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default PatientBasicInfoForm;
